import type { PropsWithChildren } from 'react';
import styled from 'styled-components';
import type { BackgroundValueType } from '@elfsight/configurator-contracts';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import { Deactivation2 } from '@elfsight/widget-deactivation';
import type { WidgetBackgroundProps } from '@elfsight/widget-ui';
import { isTransparentBackground, WidgetBackground } from '@elfsight/widget-ui';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { clamp } from 'es-toolkit';
import { meta } from '../../meta';
import { LocationCard } from '../business-profile/location-card';
import { FreeLink } from '../components/free-link';
import { useWidgetDimensions } from '../widget-dimensions-provider';
import { PADDING_BASE } from './const';

export function Layout({ children }: PropsWithChildren) {
  const settings = useSettings();
  const {
    background,
    renderThumbnailMode,
    textColor,
    width: widthSetting
  } = settings;

  const {
    ref: measureRef,
    insidePopup,
    padding,
    rect,
    layout
  } = useWidgetDimensions();

  const getBackground = (): WidgetBackgroundProps => {
    const color =
      (insidePopup || renderThumbnailMode) &&
      isTransparentBackground(background)
        ? '#fff'
        : background?.color;
    const borderRadius = clamp(
      background.borderRadius ?? 16,
      renderThumbnailMode ? 20 : 0,
      Infinity
    );
    const boxShadow = insidePopup
      ? '0 4px 16px 0 rgba(0, 0, 0, 0.3)'
      : renderThumbnailMode
        ? '0px 0px 30px 0px rgba(0, 0, 0, 0.10)'
        : undefined;

    return {
      ...background,
      color,
      borderRadius,
      verticalPadding: padding[0],
      horizontalPadding: padding[1],
      style: {
        boxShadow
      },
      wrapperStyle: {
        paddingBottom: PADDING_BASE // bottom padding is always BASE (40px)
      }
    };
  };

  const finalBackground = getBackground();
  const asideBackground = {
    type: 'color' as BackgroundValueType,
    color: 'rgba(0,0,0,0.00001)',
    borderWidth: 1,
    borderColor: getColorWithAlpha(textColor, 0.1),
    borderRadius: 16
  };

  return (
    <Deactivation2 {...meta} {...settings} childrenRef={measureRef}>
      <Container ref={measureRef}>
        {
          {
            default: (
              <WidgetBackground {...finalBackground}>
                <ContentContainer _maxWidth={960}>{children}</ContentContainer>
                <FreeLink />
              </WidgetBackground>
            ),
            oneColumnInsideBusinessCard: (
              <LocationCard
                footerOffset={rect.width <= COMMON_MOBILE_BREAKPOINT && 40}
                padding={padding}
                background={finalBackground}
              >
                <ContentContainer>{children}</ContentContainer>
                <FreeLink />
              </LocationCard>
            ),
            twoColumnsWithAsideBusinessCard: (
              <WidgetBackground {...finalBackground}>
                <RowLayoutContainer
                  _isMobile={rect.width <= COMMON_MOBILE_BREAKPOINT}
                  _autoWidth={widthSetting.auto}
                >
                  <ContentContainer _maxWidth={780}>
                    {children}
                  </ContentContainer>
                  <LocationCard
                    footerOffset={rect.width <= COMMON_MOBILE_BREAKPOINT && 40}
                    background={asideBackground}
                    aside
                    padding={[40, 40]}
                  />
                </RowLayoutContainer>

                <FreeLink />
              </WidgetBackground>
            )
          }[layout]
        }
      </Container>
    </Deactivation2>
  );
}

const RowLayoutContainer = styled.div.attrs<{
  _autoWidth?: boolean;
  _isMobile?: boolean;
}>({
  className: createReadableClass({
    component: 'layout',
    element: 'row-layout-container'
  })
})`
  position: relative;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: ${({ _isMobile }) => (_isMobile ? '40px' : '60px')};
  width: 100%;
  max-width: ${({ _autoWidth }) => _autoWidth && '1360px'};
  margin: 0 auto;
`;

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'layout',
    element: 'container'
  })
})`
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
`;

const ContentContainer = styled.div.attrs<{ _maxWidth?: number }>({
  className: createReadableClass({
    component: 'layout',
    element: 'content-container'
  })
})`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  justify-items: center;
  max-width: ${({ _maxWidth = 960 }) => _maxWidth}px;
  min-width: 0;
  width: 100%;
  margin: 0 auto;
`;
